import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { FaPlus, FaReply, FaThumbsDown, FaThumbsUp, FaTrash } from "react-icons/fa";
import { useAuth } from "../auth/AuthContext";

const TOP_BAR_BLUE = "#00005f";
const DIVIDER_GRAY = "#7a7a7a";
const SMACK_CARD_W = 260;
const SMACK_GAP = 12;
const SMACK_VISIBLE = 4;

function SmackCard({ msg, onReact, onReply, isFocused, isLeftCenter, cardW, noTransition, onDelete, currentUserId }) {
  return (
    <Box
      w={`${cardW}px`}
      flexShrink={0}
      bg="white"
      color="black"
      borderRadius="lg"
      boxShadow={isFocused ? "xl" : "md"}
      p={4}
      border={isFocused ? `2px solid ${TOP_BAR_BLUE}` : "2px solid #e2e8f0"}
      display="flex"
      flexDirection="column"
      gap={2}
      minH="140px"
      style={{
        transform: isFocused ? "scale(1.0)" : "scale(0.93)",
        opacity: isFocused ? 1 : 0.5,
        filter: isFocused ? "none" : "blur(1px)",
        transition: noTransition ? "none" : "transform 0.4s ease, opacity 0.4s ease, filter 0.4s ease",
        marginRight: isLeftCenter ? "16px" : undefined,
      }}
    >
      <Flex justify="space-between" align="center">
        <Text fontWeight="bold" fontSize="sm" color={TOP_BAR_BLUE} noOfLines={1} flex="1" mr={1}>{msg.username}</Text>
        <Flex align="center" gap={1} flexShrink={0}>
          {msg.user_id === currentUserId && (
            <IconButton
              aria-label="Delete post"
              icon={<FaTrash size={10} />}
              size="xs"
              variant="ghost"
              colorScheme="red"
              minW="auto"
              h="auto"
              p="2px"
              onClick={(e) => { e.stopPropagation(); onDelete(msg.id); }}
            />
          )}
          <Text fontSize="xs" color="gray.400">{new Date(msg.created_at).toLocaleDateString()}</Text>
        </Flex>
      </Flex>
      <Text fontSize="sm" flex="1" noOfLines={4}>{msg.body}</Text>
      <Flex align="center" gap={2} mt={1}>
        <Button size="xs" variant={msg.my_reaction === "up" ? "solid" : "ghost"}
          colorScheme={msg.my_reaction === "up" ? "green" : "gray"}
          onClick={() => onReact(msg.id, "up")} leftIcon={<FaThumbsUp />} px={2}>
          {msg.ups}
        </Button>
        <Button size="xs" variant={msg.my_reaction === "down" ? "solid" : "ghost"}
          colorScheme={msg.my_reaction === "down" ? "red" : "gray"}
          onClick={() => onReact(msg.id, "down")} leftIcon={<FaThumbsDown />} px={2}>
          {msg.downs}
        </Button>
        <Button size="xs" variant="ghost" colorScheme="blue"
          onClick={() => onReply(msg)} leftIcon={<FaReply />} ml="auto" px={2}>
          {msg.reply_count}
        </Button>
      </Flex>
    </Box>
  );
}

export default function SmackBoard({ poolId, seasonId, apiFns }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [noTransition, setNoTransition] = useState(false);
  const [showNewPost, setShowNewPost] = useState(false);
  const [showReply, setShowReply] = useState(false);
  const [activeMsg, setActiveMsg] = useState(null);
  const [replies, setReplies] = useState([]);
  const [newBody, setNewBody] = useState("");
  const [replyBody, setReplyBody] = useState("");
  const [posting, setPosting] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const [innerBoxW, setInnerBoxW] = useState(0);

  const containerRef = useRef(null);
  const innerRef = useRef(null);
  const touchStartRef = useRef(null);

  // Measure outer container for isMobile detection
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width > 0) setContainerWidth(rect.width);
    const ro = new ResizeObserver(([e]) => setContainerWidth(e.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Measure the actual overflow:hidden carousel box for cardW.
  // useLayoutEffect fires before paint so cards are sized correctly on first visible frame.
  // Re-runs when messages arrive (the box only renders when messages.length > 0).
  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width > 0) setInnerBoxW(rect.width);
    const ro = new ResizeObserver(([e]) => setInnerBoxW(e.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [messages.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const isMobile = containerWidth > 0 && containerWidth < 640;
  // Desktop carousel only activates at 4+ messages so buffer cards never appear on screen.
  // 1-3 messages on desktop: fixed 260px cards, left-aligned, static.
  const isDesktopCarousel = !isMobile && messages.length >= SMACK_VISIBLE;
  const showCarousel = isMobile ? messages.length > 1 : isDesktopCarousel;
  const visibleCount = isDesktopCarousel ? SMACK_VISIBLE : 1;
  const gap = isMobile ? 0 : SMACK_GAP;
  const cardW = isMobile
    ? Math.max(0, containerWidth - 72)
    : isDesktopCarousel
      ? (innerBoxW > 0 ? Math.floor((innerBoxW - SMACK_GAP * (SMACK_VISIBLE - 1)) / SMACK_VISIBLE) : SMACK_CARD_W)
      : SMACK_CARD_W;

  const loadMessages = useCallback(async () => {
    try {
      const data = await apiFns.getMessages(poolId, seasonId);
      setMessages(data);
    } catch {}
  }, [poolId, seasonId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (poolId && seasonId) loadMessages();
  }, [loadMessages, poolId, seasonId]);

  const displayMessages = useMemo(
    () => (showCarousel ? [...messages, ...messages.slice(0, visibleCount)] : messages),
    [messages, showCarousel, visibleCount]
  );

  useEffect(() => {
    if (isPaused || !showCarousel) return;
    const id = setInterval(() => setCurrentIndex((i) => i + 1), 10000);
    return () => clearInterval(id);
  }, [isPaused, showCarousel]);

  useEffect(() => {
    if (showCarousel && currentIndex >= messages.length) {
      const snap = setTimeout(() => {
        setNoTransition(true);
        setCurrentIndex(0);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setNoTransition(false));
        });
      }, 420);
      return () => clearTimeout(snap);
    }
  }, [currentIndex, messages.length, showCarousel]);

  async function handleDelete(msgId) {
    try {
      await apiFns.deleteMessage(poolId, msgId, seasonId);
      setMessages((prev) => prev.filter((m) => m.id !== msgId));
      setCurrentIndex(0);
    } catch {}
  }

  function handleNext() { setCurrentIndex((i) => i + 1); }
  function handlePrev() {
    if (currentIndex === 0) {
      setNoTransition(true);
      setCurrentIndex(messages.length - 1);
      setTimeout(() => setNoTransition(false), 50);
    } else {
      setCurrentIndex((i) => i - 1);
    }
  }

  // Touch swipe handlers
  function handleTouchStart(e) {
    touchStartRef.current = e.touches[0].clientX;
    setIsPaused(true);
  }
  function handleTouchEnd(e) {
    if (touchStartRef.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartRef.current;
    touchStartRef.current = null;
    if (delta < -50) handleNext();
    else if (delta > 50) handlePrev();
    setTimeout(() => setIsPaused(false), 3000);
  }

  async function openReply(msg) {
    setActiveMsg(msg);
    setReplies([]);
    setShowReply(true);
    try {
      const data = await apiFns.getReplies(poolId, msg.id, seasonId);
      setReplies(data);
    } catch {}
  }

  async function submitPost() {
    if (!newBody.trim()) return;
    setPosting(true);
    try {
      await apiFns.postMessage(poolId, newBody.trim(), seasonId);
      setNewBody("");
      setShowNewPost(false);
      await loadMessages();
      setCurrentIndex(0);
    } catch {} finally { setPosting(false); }
  }

  async function submitReply() {
    if (!replyBody.trim() || !activeMsg) return;
    setPosting(true);
    try {
      await apiFns.postReply(poolId, activeMsg.id, replyBody.trim(), seasonId);
      setReplyBody("");
      const [updatedReplies, updatedMsgs] = await Promise.all([
        apiFns.getReplies(poolId, activeMsg.id, seasonId),
        apiFns.getMessages(poolId, seasonId),
      ]);
      setReplies(updatedReplies);
      setMessages(updatedMsgs);
      setActiveMsg((prev) => ({ ...prev, reply_count: updatedReplies.length }));
    } catch {} finally { setPosting(false); }
  }

  async function handleReact(msgId, reaction) {
    try {
      await apiFns.react(poolId, msgId, reaction, seasonId);
      const updated = await apiFns.getMessages(poolId, seasonId);
      setMessages(updated);
      if (activeMsg?.id === msgId) setActiveMsg(updated.find((m) => m.id === msgId) || activeMsg);
    } catch {}
  }

  const offset = currentIndex * (cardW + gap);
  const dotIndex = currentIndex % (messages.length || 1);

  return (
    <Box
      bg={TOP_BAR_BLUE}
      borderRadius="md"
      border={`2px solid ${DIVIDER_GRAY}`}
      boxShadow="md"
      px={6}
      py={4}
      mb={4}
    >
      <Flex align="center" justify="space-between" mb={3}>
        <Heading fontFamily="'Orbitron', sans-serif" fontSize="20px"
          letterSpacing="2px" fontWeight="600" color="white">
          SMACK BOARD
        </Heading>
        <Button size="sm" leftIcon={<FaPlus />} bg="orange.400" color="white"
          _hover={{ bg: "orange.500" }} onClick={() => setShowNewPost(true)}>
          New Post
        </Button>
      </Flex>

      {/* containerRef always mounted so ResizeObserver fires on load */}
      <Box ref={containerRef}>
        {messages.length === 0 ? (
          <Text color="whiteAlpha.600" fontSize="sm" py={2}>
            No posts yet. Be the first to talk smack!
          </Text>
        ) : (
          <>
            <Box
              position="relative"
              px="44px"
              onMouseEnter={() => !isMobile && setIsPaused(true)}
              onMouseLeave={() => !isMobile && setIsPaused(false)}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              {/* Prev arrow */}
              {showCarousel && (
                <IconButton aria-label="Previous" icon={<ChevronLeftIcon boxSize={6} />}
                  position="absolute" left="0" top="50%" transform="translateY(-50%)"
                  zIndex={2} size="sm" variant="ghost" color="white"
                  _hover={{ bg: "whiteAlpha.200" }} onClick={handlePrev} />
              )}

              <Box overflow="hidden" ref={innerRef}>
                <Flex
                  gap={`${gap}px`}
                  justify="flex-start"
                  style={{
                    transform: showCarousel ? `translateX(-${offset}px)` : undefined,
                    transition: noTransition ? "none" : "transform 0.4s ease",
                  }}
                >
                  {displayMessages.map((msg, i) => (
                    <SmackCard
                      key={`${msg.id}-${i}`}
                      msg={msg}
                      onReact={handleReact}
                      onReply={openReply}
                      cardW={cardW}
                      noTransition={noTransition}
                      onDelete={handleDelete}
                      currentUserId={user?.id}
                      isFocused={
                        isDesktopCarousel
                          ? i === currentIndex + 1 || i === currentIndex + 2
                          : isMobile
                            ? i === currentIndex
                            : true
                      }
                      isLeftCenter={isDesktopCarousel && i === currentIndex + 1}
                    />
                  ))}
                </Flex>
              </Box>

              {/* Next arrow */}
              {showCarousel && (
                <IconButton aria-label="Next" icon={<ChevronRightIcon boxSize={6} />}
                  position="absolute" right="0" top="50%" transform="translateY(-50%)"
                  zIndex={2} size="sm" variant="ghost" color="white"
                  _hover={{ bg: "whiteAlpha.200" }} onClick={handleNext} />
              )}
            </Box>

            {/* Dot indicators */}
            {showCarousel && (
              <Flex justify="center" gap="6px" mt={3}>
                {messages.map((_, i) => (
                  <Box
                    key={i}
                    as="button"
                    w="8px" h="8px"
                    borderRadius="full"
                    bg={i === dotIndex ? "white" : "whiteAlpha.400"}
                    onClick={() => setCurrentIndex(i)}
                    flexShrink={0}
                  />
                ))}
              </Flex>
            )}
          </>
        )}
      </Box>

      {/* New Post Modal */}
      <Modal isOpen={showNewPost} onClose={() => setShowNewPost(false)} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color={TOP_BAR_BLUE}>New Smack Post</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4} align="stretch">
              <Textarea placeholder="Talk some smack... (500 chars max)"
                value={newBody} onChange={(e) => setNewBody(e.target.value)}
                maxLength={500} rows={4} />
              <Flex justify="flex-end" gap={2}>
                <Button variant="ghost" onClick={() => setShowNewPost(false)}>Cancel</Button>
                <Button bg={TOP_BAR_BLUE} color="white" isLoading={posting}
                  isDisabled={!newBody.trim()} onClick={submitPost}>Post</Button>
              </Flex>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Reply Thread Modal */}
      <Modal isOpen={showReply} onClose={() => setShowReply(false)} isCentered size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color={TOP_BAR_BLUE}>Replies</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack align="stretch" spacing={4}>
              {activeMsg && (
                <Box bg="gray.50" p={3} borderRadius="md" border="1px solid #e2e8f0">
                  <Flex justify="space-between" mb={1}>
                    <Text fontWeight="bold" fontSize="sm" color={TOP_BAR_BLUE}>{activeMsg.username}</Text>
                    <Text fontSize="xs" color="gray.400">{new Date(activeMsg.created_at).toLocaleDateString()}</Text>
                  </Flex>
                  <Text fontSize="sm">{activeMsg.body}</Text>
                  <Flex align="center" gap={2} mt={2}>
                    <Button size="xs" variant={activeMsg.my_reaction === "up" ? "solid" : "ghost"}
                      colorScheme={activeMsg.my_reaction === "up" ? "green" : "gray"}
                      onClick={() => handleReact(activeMsg.id, "up")} leftIcon={<FaThumbsUp />} px={2}>
                      {activeMsg.ups}
                    </Button>
                    <Button size="xs" variant={activeMsg.my_reaction === "down" ? "solid" : "ghost"}
                      colorScheme={activeMsg.my_reaction === "down" ? "red" : "gray"}
                      onClick={() => handleReact(activeMsg.id, "down")} leftIcon={<FaThumbsDown />} px={2}>
                      {activeMsg.downs}
                    </Button>
                  </Flex>
                </Box>
              )}
              {replies.length > 0 ? (
                <VStack align="stretch" spacing={2} maxH="280px" overflowY="auto">
                  {replies.map((r) => (
                    <Box key={r.id} bg="blue.50" p={3} borderRadius="md">
                      <Flex justify="space-between" mb={1}>
                        <Text fontWeight="bold" fontSize="sm" color={TOP_BAR_BLUE}>{r.username}</Text>
                        <Text fontSize="xs" color="gray.400">{new Date(r.created_at).toLocaleDateString()}</Text>
                      </Flex>
                      <Text fontSize="sm">{r.body}</Text>
                    </Box>
                  ))}
                </VStack>
              ) : (
                <Text color="gray.400" fontSize="sm">No replies yet.</Text>
              )}
              <Textarea placeholder="Write a reply..." value={replyBody}
                onChange={(e) => setReplyBody(e.target.value)} maxLength={500} rows={3} />
              <Flex justify="flex-end" gap={2}>
                <Button variant="ghost" onClick={() => setShowReply(false)}>Close</Button>
                <Button bg={TOP_BAR_BLUE} color="white" isLoading={posting}
                  isDisabled={!replyBody.trim()} onClick={submitReply}>Reply</Button>
              </Flex>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}
